<script setup lang="ts">
import {
  ElButton,
  ElCard,
  ElIcon,
  ElInput,
  ElTable,
  ElTableColumn,
} from 'element-plus'
import { Location, Search } from '@element-plus/icons-vue'
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { TrackingDevicesApi } from '~/services/apiClientGps'

const router = useRouter()
const api = new TrackingDevicesApi()
const devices = ref<any[]>([])
const searchQuery = ref('')
const loading = ref(false)

// Sort properties
const sortBy = ref('')
const sortOrder = ref('ascending')

// Computed filtered and sorted devices
const filteredDevices = computed(() => {
  let result = [...devices.value]

  // Apply search filter
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    result = result.filter(
      (device) =>
        device.external_id?.toLowerCase().includes(query) ||
        device.id.toString().includes(query)
    )
  }

  // Apply sorting
  if (sortBy.value) {
    result.sort((a, b) => {
      const aVal = a[sortBy.value]
      const bVal = b[sortBy.value]

      if (typeof aVal === 'string') {
        return sortOrder.value === 'ascending'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal)
      }

      return sortOrder.value === 'ascending' ? aVal - bVal : bVal - aVal
    })
  }

  return result
})

// Handle sort change
const handleSortChange = ({ prop, order }: { prop: string; order: string }) => {
  sortBy.value = prop
  sortOrder.value = order
}

// Handle row click to navigate to device details
const handleDeviceClick = (device: any) => {
  router.push(`/devices/${device.id}`)
}

// Load devices
const loadDevices = async () => {
  try {
    loading.value = true
    const response = await api.getTrackingDevicesTrackingDevicesGet()
    devices.value = response.data
  } catch (error) {
    console.error('Error loading devices:', error)
  } finally {
    loading.value = false
  }
}

onMounted(loadDevices)
</script>

<template>
  <div class="devices-container">
    <ElCard class="devices-card">
      <template #header>
        <div class="card-header">
          <div class="header-left">
            <ElIcon class="header-icon"><Location /></ElIcon>
            <h2>Tracking Devices</h2>
          </div>
          <div class="search-container">
            <ElInput
              v-model="searchQuery"
              placeholder="Search devices..."
              :prefix-icon="Search"
              clearable
            />
          </div>
        </div>
      </template>

      <ElTable
        :data="filteredDevices"
        style="width: 100%"
        :loading="loading"
        @sort-change="handleSortChange"
        @row-click="handleDeviceClick"
      >
        <ElTableColumn prop="id" label="ID" sortable="custom" width="100" />
        <ElTableColumn
          prop="external_id"
          label="External ID"
          sortable="custom"
        />
        <ElTableColumn fixed="right" label="Actions" width="120">
          <template #default="{ row }">
            <ElButton
              type="primary"
              size="small"
              @click.stop="handleDeviceClick(row)"
            >
              View Route
            </ElButton>
          </template>
        </ElTableColumn>
      </ElTable>
    </ElCard>
  </div>
</template>

<style scoped>
.devices-container {
  padding: 20px;
  height: calc(100vh - 100px);
  background-color: #f5f7fa;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.devices-card {
  height: 100%;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  flex-shrink: 0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-icon {
  font-size: 24px;
  color: var(--el-color-primary);
}

.header-left h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #2c3e50;
}

.search-container {
  width: 300px;
}

:deep(.el-card__body) {
  height: 100%;
  padding: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

:deep(.el-table) {
  flex: 1;
  overflow: auto;
}

:deep(.el-table__inner-wrapper) {
  height: 100%;
}

:deep(.el-table__body-wrapper) {
  overflow-y: auto;
}

:deep(.el-table__row) {
  cursor: pointer;
}

:deep(.el-table__row:hover) {
  background-color: var(--el-color-primary-light-9) !important;
}

:deep(.el-table__fixed-right) {
  height: 100% !important;
}

:deep(.el-table__fixed-right-patch) {
  background: #f5f7fa;
}
</style>
